import { useEffect, useMemo, useRef, useState } from "react";
import "../../styles/controls.css";
import "../../styles/layout.css";
import "../../styles/tokens.css";
import {
  ClassroomTools,
  RevealAnswersButton,
} from "../../components/ClassroomTools";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { ExhibitHeader } from "../../components/ExhibitHeader";
import { FullscreenToggle } from "../../components/FullscreenToggle";
import { StatusNotice } from "../../components/StatusNotice";
import {
  readStoredPreference,
  writeStoredPreference,
} from "../../hooks/localPrefs";
import type { SceneQuality } from "../../scene/SceneCanvas";
import { DemoCanvas } from "../shared/DemoCanvas";
import { DemoTour, type DemoTourStep } from "../shared/DemoTour";
import {
  readHashNumber,
  readHashParams,
  writeHashParams,
} from "../shared/hashState";
import { PLAYBACK_SPEEDS, usePlaybackClock } from "../shared/usePlaybackClock";
import { HorizonFallback } from "./HorizonFallback";
import { HorizonScene } from "./HorizonScene";
import {
  DAY_HOURS,
  HORIZON_SECONDS_AT_ONE_X,
  HORIZON_SITES,
  NOON_HOUR,
  compassPoint,
  formatClockHour,
  getHorizonSnapshot,
  type HorizonSeason,
} from "./geometry";

function localFallbackRequested() {
  return (
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("fallback") === "1"
  );
}

const SITE_STORAGE_KEY = "pupil:horizon:site";
const SEASON_STORAGE_KEY = "pupil:horizon:season";
const SPEED_STORAGE_KEY = "pupil:horizon:speed";
const DEFAULT_SITE_ID = "pretoria";

function isKnownSiteId(value: string | null): value is string {
  return value !== null && HORIZON_SITES.some((site) => site.id === value);
}

function resolveInitialSiteId(): string {
  // A shared lesson link wins over the teacher's last-used spot.
  const fromUrl = readHashParams().get("site");
  if (isKnownSiteId(fromUrl)) {
    return fromUrl;
  }
  const stored = readStoredPreference(SITE_STORAGE_KEY);
  return isKnownSiteId(stored) ? stored : DEFAULT_SITE_ID;
}

function resolveInitialSeason(): HorizonSeason {
  const fromUrl = readHashParams().get("season");
  if (fromUrl === "june" || fromUrl === "december") {
    return fromUrl;
  }
  return readStoredPreference(SEASON_STORAGE_KEY) === "december"
    ? "december"
    : "june";
}

function resolveInitialHour(): number {
  const fromUrl = readHashNumber("hour");
  return fromUrl !== null && fromUrl >= 0 && fromUrl <= DAY_HOURS
    ? fromUrl
    : NOON_HOUR;
}

export function HorizonApp({
  forceFallback = false,
}: {
  readonly forceFallback?: boolean;
}) {
  const [fallbackActive, setFallbackActive] = useState(
    () => forceFallback || localFallbackRequested(),
  );
  const [sceneQuality, setSceneQuality] = useState<SceneQuality | null>(null);
  const [readoutVisible, setReadoutVisible] = useState(false);
  const [classroomMode, setClassroomMode] = useState(false);
  const [predictMode, setPredictMode] = useState(false);
  const [answersRevealed, setAnswersRevealed] = useState(false);
  const [siteId, setSiteIdState] = useState(resolveInitialSiteId);
  const [season, setSeasonState] =
    useState<HorizonSeason>(resolveInitialSeason);
  const [viewCue, setViewCue] = useState(0);
  const bumpView = () => setViewCue((cue) => cue + 1);
  const layoutRef = useRef<HTMLElement | null>(null);
  const clock = usePlaybackClock({
    cycleLength: DAY_HOURS,
    initialValue: resolveInitialHour(),
    secondsPerCycleAtOneX: HORIZON_SECONDS_AT_ONE_X,
    speedStorageKey: SPEED_STORAGE_KEY,
  });
  const snapshot = useMemo(
    () => getHorizonSnapshot(siteId, season, clock.value),
    [clock.value, season, siteId],
  );
  const setSiteId = (nextSiteId: string) => {
    setSiteIdState(nextSiteId);
    writeStoredPreference(SITE_STORAGE_KEY, nextSiteId);
  };
  const setSeason = (nextSeason: HorizonSeason) => {
    setSeasonState(nextSeason);
    writeStoredPreference(SEASON_STORAGE_KEY, nextSeason);
  };
  const togglePredictMode = () => {
    setPredictMode((current) => !current);
    setAnswersRevealed(false);
  };

  // Keep the URL shareable: whenever the day is paused or scrubbed, the hash
  // carries the exact setup (site, season, hour) for the next teacher. The
  // initial state above already reflects any incoming link, so the write can
  // never clobber it with defaults.
  useEffect(() => {
    if (clock.isPlaying) {
      return;
    }
    writeHashParams({
      hour: clock.value.toFixed(1),
      season,
      site: siteId,
    });
  }, [clock.isPlaying, clock.value, season, siteId]);
  const pauseClock = () => {
    if (clock.isPlaying) {
      clock.togglePlayback();
    }
  };
  const tourSteps: readonly DemoTourStep[] = [
    {
      caption:
        "You are standing at the centre of this sky. The ring around you is your horizon, and the yellow arc is the Sun's path for this whole day.",
      onEnter: () => {
        pauseClock();
        clock.setValue(NOON_HOUR);
        bumpView();
      },
    },
    {
      caption:
        "The day starts with the Sun coming up on the eastern side of your sky. Press Play later to watch it climb from here.",
      onEnter: () => {
        pauseClock();
        clock.setValue(snapshot.sunriseHour ?? 6);
        bumpView();
      },
    },
    {
      caption:
        "At noon the Sun is at its highest for the day. Your latitude and the season set that height — try another 'Stand here' spot and compare.",
      onEnter: () => {
        pauseClock();
        clock.setValue(NOON_HOUR);
        bumpView();
      },
    },
  ];

  return (
    <main className="app-shell">
      <ExhibitHeader
        chain="Where you stand → your horizon and zenith → how the Sun crosses the sky"
        demoNumber="003"
        readoutVisible={readoutVisible}
        title="Your Horizon and Altitude"
        onToggleReadout={() => setReadoutVisible((visible) => !visible)}
      />
      <section
        aria-label="Local horizon simulation"
        className={`exhibit-layout${readoutVisible ? "" : " readout-hidden"}`}
        ref={layoutRef}
      >
        <section className="scene-region" aria-label="Local sky view">
          <StatusNotice quality={fallbackActive ? null : sceneQuality} />
          <div
            className="scene-slot horizon-scene-slot"
            data-testid="horizon-scene-slot"
          >
            <FullscreenToggle targetRef={layoutRef} />
            <DemoTour steps={tourSteps} />
            {fallbackActive ? (
              <HorizonFallback snapshot={snapshot} />
            ) : (
              <ErrorBoundary
                fallbackDescription="Continue with the same horizon-and-altitude geometry in the interactive 2D teaching view."
                onUseFallback={() => setFallbackActive(true)}
              >
                <DemoCanvas
                  ariaLabel={`Local sky from ${snapshot.site.name}`}
                  cameraFov={72}
                  cameraPosition={[0, 1.6, -5.4]}
                  onFallbackNeeded={() => setFallbackActive(true)}
                  onQualityChange={setSceneQuality}
                >
                  <HorizonScene
                    classroomMode={classroomMode}
                    snapshot={snapshot}
                    viewCue={viewCue}
                  />
                </DemoCanvas>
              </ErrorBoundary>
            )}
          </div>
          {forceFallback ? null : (
            <p className="fallback-switch">
              <button
                type="button"
                onClick={() => setFallbackActive((active) => !active)}
              >
                {fallbackActive
                  ? "Try the full 3D view"
                  : "Use the simple 2D view"}
              </button>
              {fallbackActive ? null : (
                <span> — for older devices or to save data.</span>
              )}
            </p>
          )}
          <p className="orientation-note">
            The sky from {snapshot.site.name}. Play the day to watch the Sun
            rise, cross the meridian, and set
            {fallbackActive ? "." : " — drag the view to look around the sky."}
          </p>
        </section>
        {readoutVisible ? (
          <aside
            aria-label="Notes"
            className={`teaching-panel${
              predictMode && !answersRevealed ? " predict-mode" : ""
            }`}
          >
            <section className="information-panel">
              <h2>Notes</h2>
              {predictMode ? (
                <RevealAnswersButton
                  revealed={answersRevealed}
                  onToggle={() => setAnswersRevealed((current) => !current)}
                />
              ) : null}
              <p>
                <span aria-label="Selected site">{snapshot.site.name}</span>
              </p>
              <p>{snapshot.site.blurb}</p>
              <dl>
                <div>
                  <dt>Local time</dt>
                  <dd>{formatClockHour(snapshot.hourOfDay)}</dd>
                </div>
                <div>
                  <dt>Latitude</dt>
                  <dd>{snapshot.site.latitudeDegrees.toFixed(1)}°</dd>
                </div>
                <div>
                  <dt>Sun altitude</dt>
                  <dd>
                    {snapshot.sunAboveHorizon
                      ? `${Math.round(snapshot.altitudeDegrees)}°`
                      : "Below horizon"}
                  </dd>
                </div>
                <div>
                  <dt>Sun direction</dt>
                  <dd>
                    {snapshot.sunAboveHorizon
                      ? `${compassPoint(snapshot.azimuthDegrees)} · ${Math.round(snapshot.azimuthDegrees)}°`
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Day length</dt>
                  <dd>
                    {snapshot.polarCondition === "midnight-sun"
                      ? "Midnight sun"
                      : snapshot.polarCondition === "polar-night"
                        ? "Polar night"
                        : `${snapshot.dayLengthHours.toFixed(1)} h`}
                  </dd>
                </div>
                <div>
                  <dt>Season</dt>
                  <dd>
                    {season === "june" ? "June solstice" : "December solstice"}
                  </dd>
                </div>
              </dl>
              <p>
                The nearer you stand to the latitude where the Sun is overhead,
                the higher your noon Sun. At a pole it never gets more than
                23.4° up — and for half the year it never rises.
              </p>
            </section>
          </aside>
        ) : null}
        <section className="controls-panel" aria-label="Simulation controls">
          <fieldset className="control-group playback-controls">
            <legend>Day</legend>
            <div className="button-row">
              <button
                aria-pressed={clock.isPlaying}
                type="button"
                onClick={clock.togglePlayback}
              >
                {clock.isPlaying ? "Pause day" : "Play day"}
              </button>
              <button
                type="button"
                onClick={() => {
                  clock.setValue(NOON_HOUR);
                  bumpView();
                }}
              >
                Noon
              </button>
              {snapshot.sunriseHour !== null ? (
                <button
                  type="button"
                  onClick={() => {
                    clock.setValue(snapshot.sunriseHour ?? 6);
                    bumpView();
                  }}
                >
                  Sunrise
                </button>
              ) : null}
              {snapshot.sunsetHour !== null ? (
                <button
                  type="button"
                  onClick={() => {
                    clock.setValue(snapshot.sunsetHour ?? 18);
                    bumpView();
                  }}
                >
                  Sunset
                </button>
              ) : null}
            </div>
            <label className="select-control" htmlFor="horizon-speed">
              Simulation speed
              <select
                id="horizon-speed"
                value={clock.speed}
                onChange={(event) =>
                  clock.setSpeed(
                    Number(
                      event.currentTarget.value,
                    ) as (typeof PLAYBACK_SPEEDS)[number],
                  )
                }
              >
                {PLAYBACK_SPEEDS.map((speed) => (
                  <option key={speed} value={speed}>
                    {speed}×
                  </option>
                ))}
              </select>
            </label>
          </fieldset>
          <section className="timeline" aria-label="Local time">
            <label htmlFor="local-hour">Local time</label>
            <input
              aria-label="Local time"
              id="local-hour"
              max={DAY_HOURS}
              min={0}
              step={0.05}
              type="range"
              value={clock.value}
              onChange={(event) =>
                clock.setValue(Number(event.currentTarget.value))
              }
            />
            <span className="timeline-value">
              {formatClockHour(clock.value)}
            </span>
          </section>
          <fieldset className="control-group">
            <legend>Season</legend>
            <div className="button-row">
              <button
                aria-current={season === "june"}
                type="button"
                onClick={() => {
                  setSeason("june");
                  bumpView();
                }}
              >
                June solstice
              </button>
              <button
                aria-current={season === "december"}
                type="button"
                onClick={() => {
                  setSeason("december");
                  bumpView();
                }}
              >
                December solstice
              </button>
            </div>
          </fieldset>
          <fieldset className="control-group">
            <legend>Stand here</legend>
            <ul className="phase-grid">
              {HORIZON_SITES.map((site) => (
                <li key={site.id}>
                  <button
                    aria-current={site.id === siteId}
                    type="button"
                    onClick={() => {
                      setSiteId(site.id);
                      bumpView();
                    }}
                  >
                    {site.name}
                  </button>
                </li>
              ))}
            </ul>
          </fieldset>
          <ClassroomTools
            classroomMode={classroomMode}
            predictMode={predictMode}
            onToggleClassroomMode={() =>
              setClassroomMode((current) => !current)
            }
            onTogglePredictMode={togglePredictMode}
          />
        </section>
        <p className="scale-note">
          Educational sky — the dome is nearby so the path is readable.
          Altitude, azimuth, and day length are the real lesson.
        </p>
      </section>
    </main>
  );
}
